/**
 * Callout — an aside / note card (used for About asides like "If you disagree
 * with anything here — tell me"). Composes the Card primitive with a tone-tinted
 * left accent + a per-tone lucide icon. Tokens + Tailwind only.
 */
import type { ReactNode } from 'react';
import { Info, AlertTriangle, CheckCircle2, Lightbulb } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '../card';
import { cn } from '../../utils';
import { inlineFormat } from './Prose';

/** Compiled-markdown content arrives as a string — render its inline formatting
 *  (**bold**, *italic*, `code`, links) instead of showing literal asterisks.
 *  Non-string ReactNode children pass through untouched. */
function richText(node: ReactNode): ReactNode {
  return typeof node === 'string'
    ? <span dangerouslySetInnerHTML={{ __html: inlineFormat(node) }} />
    : node;
}

export type CalloutVariant = 'note' | 'info' | 'warning' | 'success';

export interface CalloutProps {
  readonly variant?: CalloutVariant;
  readonly title?: ReactNode;
  readonly content: ReactNode;
  readonly children?: ReactNode;
}

const TONE: Record<CalloutVariant, { accent: string; icon: LucideIcon; iconColor: string }> = {
  note: { accent: 'border-l-border', icon: Lightbulb, iconColor: 'text-muted-foreground' },
  info: { accent: 'border-l-primary', icon: Info, iconColor: 'text-primary' },
  warning: { accent: 'border-l-warning', icon: AlertTriangle, iconColor: 'text-warning' },
  success: { accent: 'border-l-success', icon: CheckCircle2, iconColor: 'text-success' },
};

export function Callout({ variant = 'note', title, content, children }: CalloutProps) {
  const tone = TONE[variant];
  const Icon = tone.icon;
  return (
    <Card className={cn('border-l-4 p-(--space-card)', tone.accent)}>
      <div className="flex gap-3">
        <Icon className={cn('mt-0.5 size-5 shrink-0', tone.iconColor)} aria-hidden="true" />
        <div className="min-w-0">
          {title && <p className="mb-1 font-semibold text-foreground">{richText(title)}</p>}
          <div className="text-muted-foreground [&>p]:mb-2 [&>p:last-child]:mb-0">{richText(content) ?? children}</div>
        </div>
      </div>
    </Card>
  );
}
